import React from 'react'

import {
    Button,
    Text,
    RadioGroup,
    Radio,
    useMultiStyleConfig,
    useStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useIntl} from 'react-intl'
import {useHistory} from 'react-router-dom'
import PropTypes from 'prop-types'
import {Menu, MenuButton, MenuItem, MenuList} from '@chakra-ui/react'
import {ChevronDown} from '../../../components/icons'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'

const SortByButton = ({
    selectedSortingOptionLabel,
    sortUrls,
    productSearchResult,
    onSelect = noop,
}) => {
    const {formatMessage} = useIntl()
    const styles = useMultiStyleConfig('Filters')
    const menuItemsStyle = useStyleConfig('SortByAccordionItem', {variant: 'sortByButton'})
    const history = useHistory()
    const [value, setValue] = React.useState(selectedSortingOptionLabel?.label)
    const menuItemRefs = React.useRef([])
    const menuButtonRef = React.useRef(null)
    const [focusedIndex, setFocusedIndex] = React.useState(0)

    React.useEffect(() => {
        // Focus first item when menu opens
        if (menuItemRefs.current[0]) {
            setTimeout(() => {
                menuItemRefs.current[0]?.focus()
            }, 0)
        }
    }, [])

    const handleChange = (newValue) => {
        const selectedOption = productSearchResult?.sortingOptions.find(
            (option) => option.label === newValue,
        )
        const selectedUrl = sortUrls[productSearchResult?.sortingOptions.indexOf(selectedOption)]
        setValue(newValue)
        history.push(selectedUrl)
        onSelect()
    }

    const handleMenuListKeyDown = (e, onClose) => {
        if (e.key === 'Tab') {
            e.preventDefault()
            e.stopPropagation()

            const currentIndex = focusedIndex
            const isLastItem = currentIndex === productSearchResult?.sortingOptions?.length - 1
            const isFirstItem = currentIndex === 0

            if (e.shiftKey) {
                if (isFirstItem) {
                    onClose()
                    setTimeout(() => {
                        menuButtonRef.current?.focus()
                    }, 0)
                } else {
                    const prevIdx = currentIndex - 1
                    setFocusedIndex(prevIdx)
                    menuItemRefs.current[prevIdx]?.focus()
                }
            } else {
                if (isLastItem) {
                    onClose()
                } else {
                    const nextIdx = currentIndex + 1
                    setFocusedIndex(nextIdx)
                    menuItemRefs.current[nextIdx]?.focus()
                }
            }
        }
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Tab') {
            e.preventDefault()
            e.stopPropagation()
        }
    }

    const handleMenuItemFocus = (idx) => {
        setFocusedIndex(idx)
    }

    return (
        <Menu>
            {({isOpen, onClose}) => (
                <>
                    <MenuButton
                        as={Button}
                        ref={menuButtonRef}
                        rightIcon={
                            <ChevronDown
                                {...styles.filterIcon}
                                style={{transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}}
                            />
                        }
                        {...styles.filterButton}
                        {...styles.refinementsSelectorButton}
                        _focusVisible={{
                            border: 'small',
                            borderColor: 'button.border.primary-focused',
                            borderStyle: 'solid',
                        }}
                    >
                        <Text {...styles.filterText}>
                            {formatMessage(
                                {id: 'product_list.button.order_by', defaultMessage: 'Order by'},
                                {sortOption: selectedSortingOptionLabel?.label},
                            )}
                        </Text>
                    </MenuButton>
                    <MenuList
                        {...styles.sortByFilterMenuList}
                        onKeyDown={(e) => handleMenuListKeyDown(e, onClose)}
                    >
                        <RadioGroup
                            onChange={handleChange}
                            value={value}
                            {...menuItemsStyle?.menuContainer}
                        >
                            {productSearchResult?.sortingOptions?.map((option, idx) => (
                                <MenuItem
                                    key={idx}
                                    ref={(el) => (menuItemRefs.current[idx] = el)}
                                    onKeyDown={(e) => handleKeyDown(e, idx, onClose)}
                                    onFocus={() => handleMenuItemFocus(idx)}
                                    tabIndex={0}
                                    {...menuItemsStyle.menuItem}
                                    _focusVisible={{boxShadow: 'outline', bg: 'none'}}
                                    _hover={{bg: 'none'}}
                                    _focus={{bg: 'none'}}
                                >
                                    <Radio
                                        value={option.label}
                                        {...menuItemsStyle.radio}
                                        tabIndex={-1}
                                    >
                                        <Text {...menuItemsStyle?.text}>{option.label}</Text>
                                    </Radio>
                                </MenuItem>
                            ))}
                        </RadioGroup>
                    </MenuList>
                </>
            )}
        </Menu>
    )
}

SortByButton.propTypes = {
    selectedSortingOptionLabel: PropTypes.shape({
        label: PropTypes.string,
    }),
    sortUrls: PropTypes.arrayOf(PropTypes.string).isRequired,
    productSearchResult: PropTypes.shape({
        sortingOptions: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string,
            }),
        ),
    }),
    onSelect: PropTypes.func,
}

export default SortByButton
